import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import AdminRichTextEditor from '../../components/admin/AdminRichTextEditor'
import {
  AdminApi,
  AdminQuestionAnswer,
  AdminQuestionGroup,
  AdminQuestionLevel,
  AdminQuestionTopic,
  AdminQuestionType,
} from '../../services/adminApi'

type AnswerDraft = {
  key: string
  id?: number
  content: string
  isCorrect: boolean
}

type QuestionKind = 'standalone' | 'group'
type GroupMode = 'existing' | 'new'

const emptyAnswer = (): AnswerDraft => ({
  key: `a-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  content: '',
  isCorrect: false,
})

const stripHtml = (html: string) =>
  String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const fieldClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-700 focus:outline-none focus:ring-1 focus:ring-emerald-700'
const labelClass = 'mb-1 block text-xs font-semibold text-slate-700'
const panelClass = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm'
const btnPrimary =
  'rounded-lg bg-emerald-800 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-900 disabled:opacity-50'
const btnSecondary =
  'rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50'

const AdminQuestionFormPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isEdit = Boolean(id)
  const questionId = id ? Number(id) : null

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [topics, setTopics] = useState<AdminQuestionTopic[]>([])
  const [groups, setGroups] = useState<AdminQuestionGroup[]>([])

  const [kind, setKind] = useState<QuestionKind>(
    searchParams.get('kind') === 'group' || searchParams.get('groupId') ? 'group' : 'standalone'
  )
  const [groupMode, setGroupMode] = useState<GroupMode>(
    searchParams.get('groupId') ? 'existing' : 'new'
  )
  const [caseStudyHtml, setCaseStudyHtml] = useState('')
  const [selectedGroupPreview, setSelectedGroupPreview] = useState('')

  const [content, setContent] = useState('')
  const [questionType, setQuestionType] = useState<AdminQuestionType>('SINGLE_CHOICE')
  const [level, setLevel] = useState<AdminQuestionLevel>('MEDIUM')
  const [point, setPoint] = useState(1)
  const [topicId, setTopicId] = useState<number | ''>('')
  const [groupId, setGroupId] = useState<number | ''>('')
  const [sortGroup, setSortGroup] = useState<number | ''>('')
  const [helpContent, setHelpContent] = useState('')
  const [essayAnswer, setEssayAnswer] = useState('')
  const [isActive, setIsActive] = useState(1)
  const [answers, setAnswers] = useState<AnswerDraft[]>([emptyAnswer(), emptyAnswer()])

  const isChoice =
    questionType === 'SINGLE_CHOICE' ||
    questionType === 'MULTIPLE_CHOICE' ||
    questionType === 'CHOICE'
  const isMulti = questionType === 'MULTIPLE_CHOICE'

  useEffect(() => {
    const presetGroup = searchParams.get('groupId')
    if (presetGroup && !isEdit) {
      setKind('group')
      setGroupMode('existing')
      setGroupId(Number(presetGroup))
    }
  }, [searchParams, isEdit])

  useEffect(() => {
    ;(async () => {
      try {
        const [tRes, gRes] = await Promise.all([
          AdminApi.listQuestionTopics(),
          AdminApi.listQuestionGroups({ limit: 100 }),
        ])
        setTopics(tRes.data || [])
        setGroups(gRes.data?.items || [])
      } catch {
        toast.error('Không tải được chủ đề / nhóm')
      }
    })()
  }, [])

  useEffect(() => {
    if (kind !== 'group' || groupMode !== 'existing' || groupId === '') {
      setSelectedGroupPreview('')
      return
    }
    const found = groups.find((g) => g.id === Number(groupId))
    if (found?.name) {
      setSelectedGroupPreview(found.name)
      return
    }
    ;(async () => {
      try {
        const res = await AdminApi.getQuestionGroup(Number(groupId))
        setSelectedGroupPreview(res.data.name || '')
      } catch {
        setSelectedGroupPreview('')
      }
    })()
  }, [kind, groupMode, groupId, groups])

  useEffect(() => {
    if (!isEdit || !questionId) return
    ;(async () => {
      setLoading(true)
      try {
        const res = await AdminApi.getQuestion(questionId)
        const q = res.data
        setContent(q.content || '')
        const qt = String(q.questionType || 'SINGLE_CHOICE').toUpperCase()
        setQuestionType(qt === 'CHOICE' ? 'SINGLE_CHOICE' : (qt as AdminQuestionType))
        setLevel((String(q.level || 'MEDIUM').toUpperCase() as AdminQuestionLevel) || 'MEDIUM')
        setPoint(q.point != null ? Number(q.point) : 1)
        setTopicId(q.topicId ?? '')
        setGroupId(q.groupId ?? '')
        setKind(q.groupId ? 'group' : 'standalone')
        setGroupMode('existing')
        setSortGroup(q.sortGroup ?? '')
        setHelpContent(q.helpContent || '')
        setEssayAnswer(q.essayAnswer || '')
        setIsActive(q.isActive ? 1 : 0)
        const ans = (q.answers || []) as AdminQuestionAnswer[]
        if (ans.length > 0) {
          setAnswers(
            ans.map((a) => ({
              key: `a-${a.id}`,
              id: a.id,
              content: a.content || '',
              isCorrect: Boolean(a.isCorrect),
            }))
          )
        } else {
          setAnswers([emptyAnswer(), emptyAnswer()])
        }
      } catch {
        toast.error('Không tải được câu hỏi')
        navigate('/admin/question-bank')
      } finally {
        setLoading(false)
      }
    })()
  }, [isEdit, questionId, navigate])

  const title = useMemo(
    () => (isEdit ? `Sửa câu hỏi #${questionId}` : 'Tạo câu hỏi'),
    [isEdit, questionId]
  )

  const setCorrect = (key: string, checked: boolean) => {
    setAnswers((prev) =>
      prev.map((a) => {
        if (isMulti) {
          return a.key === key ? { ...a, isCorrect: checked } : a
        }
        return { ...a, isCorrect: a.key === key ? checked : false }
      })
    )
  }

  const resolveGroupId = async (): Promise<number | false> => {
    if (kind !== 'group') return false

    if (isEdit || groupMode === 'existing') {
      if (groupId === '') {
        toast.error('Chọn case study cho câu hỏi nhóm')
        return false
      }
      return Number(groupId)
    }

    if (!stripHtml(caseStudyHtml)) {
      toast.error('Nhập nội dung case study (HTML)')
      return false
    }
    const gRes = await AdminApi.createQuestionGroup({ name: caseStudyHtml })
    if (!gRes.success || !gRes.data?.id) {
      toast.error(gRes.message || 'Tạo case study thất bại')
      return false
    }
    return gRes.data.id
  }

  const save = async () => {
    if (!stripHtml(content)) {
      toast.error('Nội dung câu hỏi không được trống')
      return
    }

    let payloadAnswers:
      | Array<{ id?: number; content: string; isCorrect: boolean }>
      | undefined

    if (isChoice) {
      const filled = answers.filter((a) => stripHtml(a.content))
      if (filled.length < 2) {
        toast.error('Trắc nghiệm cần ít nhất 2 đáp án')
        return
      }
      const correctCount = filled.filter((a) => a.isCorrect).length
      if (isMulti && correctCount < 1) {
        toast.error('Cần ít nhất 1 đáp án đúng')
        return
      }
      if (!isMulti && correctCount !== 1) {
        toast.error('Cần đúng 1 đáp án đúng')
        return
      }
      payloadAnswers = filled.map((a) => ({
        id: a.id,
        content: a.content,
        isCorrect: a.isCorrect,
      }))
    } else {
      payloadAnswers = []
    }

    setSaving(true)
    try {
      let resolvedGroupId: number | null = null
      if (kind === 'group') {
        const gid = await resolveGroupId()
        if (gid === false) {
          setSaving(false)
          return
        }
        resolvedGroupId = gid
      }

      const payload = {
        content,
        questionType: questionType === 'CHOICE' ? 'SINGLE_CHOICE' : questionType,
        level,
        point,
        topicId: topicId === '' ? null : Number(topicId),
        groupId: kind === 'group' ? resolvedGroupId : null,
        sortGroup: kind === 'group' && sortGroup !== '' ? Number(sortGroup) : null,
        helpContent: helpContent || null,
        essayAnswer: questionType === 'ESSAY' ? essayAnswer || null : null,
        essayAnswerType: questionType === 'ESSAY' ? 'TEXT' : null,
        isActive,
        answers: payloadAnswers,
      }

      const res =
        isEdit && questionId
          ? await AdminApi.updateQuestion(questionId, payload)
          : await AdminApi.createQuestion(payload)

      if (res.success) {
        toast.success(res.message || 'Đã lưu')
        if (kind === 'group' && resolvedGroupId) {
          navigate(`/admin/question-bank?tab=case-study&groupId=${resolvedGroupId}`)
        } else {
          navigate('/admin/question-bank')
        }
      } else {
        toast.error(res.message || 'Lưu thất bại')
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Lưu thất bại'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Đang tải…</p>
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/question-bank" className="text-sm font-medium text-emerald-800 hover:underline">
            ← Ngân hàng câu hỏi
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-slate-900">{title}</h1>
        </div>
        <button type="button" disabled={saving} onClick={() => void save()} className={btnPrimary}>
          {saving ? 'Đang lưu…' : 'Lưu câu hỏi'}
        </button>
      </div>

      {/* Chọn câu lẻ / câu nhóm */}
      <div className={`${panelClass} space-y-3`}>
        <p className={labelClass}>Hình thức câu hỏi</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => {
              setKind('standalone')
              setGroupId('')
            }}
            className={`rounded-lg border-2 px-4 py-3 text-left transition-colors ${
              kind === 'standalone'
                ? 'border-emerald-800 bg-emerald-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <span className="block text-sm font-bold text-slate-900">Câu hỏi lẻ</span>
            <span className="mt-0.5 block text-xs text-slate-600">
              Câu độc lập, không gắn tình huống chung.
            </span>
          </button>
          <button
            type="button"
            onClick={() => setKind('group')}
            className={`rounded-lg border-2 px-4 py-3 text-left transition-colors ${
              kind === 'group'
                ? 'border-emerald-800 bg-emerald-50'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <span className="block text-sm font-bold text-slate-900">Câu hỏi nhóm (case study)</span>
            <span className="mt-0.5 block text-xs text-slate-600">
              Gắn vào tình huống HTML dùng chung cho nhiều câu.
            </span>
          </button>
        </div>

        {kind === 'group' && (
          <div className="space-y-3 border-t border-slate-100 pt-3">
            {!isEdit && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setGroupMode('new')}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                    groupMode === 'new'
                      ? 'bg-emerald-800 text-white'
                      : 'border border-slate-300 bg-white text-slate-700'
                  }`}
                >
                  Tạo case study mới
                </button>
                <button
                  type="button"
                  onClick={() => setGroupMode('existing')}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                    groupMode === 'existing'
                      ? 'bg-emerald-800 text-white'
                      : 'border border-slate-300 bg-white text-slate-700'
                  }`}
                >
                  Chọn case study có sẵn
                </button>
              </div>
            )}

            {groupMode === 'new' && !isEdit ? (
              <div>
                <label className={labelClass}>Nội dung case study (HTML)</label>
                <AdminRichTextEditor
                  value={caseStudyHtml}
                  onChange={setCaseStudyHtml}
                  placeholder="Nhập tình huống / đoạn văn case study…"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <label className={labelClass}>Case study</label>
                  <select
                    value={groupId}
                    onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : '')}
                    className={fieldClass}
                  >
                    <option value="">— Chọn case study —</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        #{g.id} — {g.preview || 'Case study'}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedGroupPreview && (
                  <div>
                    <p className="mb-1 text-xs font-semibold text-slate-600">Xem trước tình huống</p>
                    <div
                      className="prose prose-sm max-w-none rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-800"
                      dangerouslySetInnerHTML={{ __html: selectedGroupPreview }}
                    />
                    {groupId !== '' && (
                      <Link
                        to={`/admin/question-bank?tab=case-study&groupId=${groupId}`}
                        className="mt-1 inline-block text-xs font-medium text-emerald-800 hover:underline"
                      >
                        Mở trang quản lý case study →
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="max-w-xs">
              <label className={labelClass}>Thứ tự trong nhóm (tuỳ chọn)</label>
              <input
                type="number"
                min={0}
                value={sortGroup}
                onChange={(e) => setSortGroup(e.target.value === '' ? '' : Number(e.target.value))}
                className={fieldClass}
              />
            </div>
          </div>
        )}
      </div>

      <div className={`grid gap-4 sm:grid-cols-2 ${panelClass}`}>
        <div>
          <label className={labelClass}>Loại câu hỏi</label>
          <select
            value={questionType}
            onChange={(e) => setQuestionType(e.target.value as AdminQuestionType)}
            className={fieldClass}
          >
            <option value="SINGLE_CHOICE">Trắc nghiệm 1 đáp án</option>
            <option value="MULTIPLE_CHOICE">Trắc nghiệm nhiều đáp án</option>
            <option value="ESSAY">Tự luận</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Độ khó</label>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value as AdminQuestionLevel)}
            className={fieldClass}
          >
            <option value="EASY">EASY</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="HARD">HARD</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Điểm mặc định</label>
          <input
            type="number"
            min={0}
            step={0.5}
            value={point}
            onChange={(e) => setPoint(Number(e.target.value))}
            className={fieldClass}
          />
        </div>
        <div>
          <label className={labelClass}>Trạng thái</label>
          <select
            value={isActive}
            onChange={(e) => setIsActive(Number(e.target.value))}
            className={fieldClass}
          >
            <option value={1}>Active</option>
            <option value={0}>Tắt</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>Chủ đề</label>
          <select
            value={topicId}
            onChange={(e) => setTopicId(e.target.value ? Number(e.target.value) : '')}
            className={fieldClass}
          >
            <option value="">— Không —</option>
            {topics.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={panelClass}>
        <label className="mb-2 block text-sm font-semibold text-slate-800">
          Nội dung câu hỏi (HTML)
        </label>
        <AdminRichTextEditor value={content} onChange={setContent} placeholder="Nhập đề bài…" />
      </div>

      {isChoice && (
        <div className={`space-y-3 ${panelClass}`}>
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-slate-900">Đáp án</h2>
            <button
              type="button"
              onClick={() => setAnswers((prev) => [...prev, emptyAnswer()])}
              className={btnSecondary}
            >
              + Thêm đáp án
            </button>
          </div>
          {answers.map((ans, idx) => (
            <div key={ans.key} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-800">
                  <input
                    type={isMulti ? 'checkbox' : 'radio'}
                    name="correct-answer"
                    checked={ans.isCorrect}
                    onChange={(e) => setCorrect(ans.key, e.target.checked)}
                  />
                  Đáp án {idx + 1} {ans.isCorrect ? '(đúng)' : ''}
                </label>
                {answers.length > 2 && (
                  <button
                    type="button"
                    onClick={() => setAnswers((prev) => prev.filter((a) => a.key !== ans.key))}
                    className="text-xs font-semibold text-red-600 hover:underline"
                  >
                    Xóa
                  </button>
                )}
              </div>
              <AdminRichTextEditor
                value={ans.content}
                onChange={(html) =>
                  setAnswers((prev) =>
                    prev.map((a) => (a.key === ans.key ? { ...a, content: html } : a))
                  )
                }
                placeholder={`Nội dung đáp án ${idx + 1}`}
              />
            </div>
          ))}
        </div>
      )}

      {questionType === 'ESSAY' && (
        <div className={panelClass}>
          <label className="mb-2 block text-sm font-semibold text-slate-800">
            Đáp án mẫu / gợi ý chấm (HTML)
          </label>
          <AdminRichTextEditor
            value={essayAnswer}
            onChange={setEssayAnswer}
            placeholder="Đáp án tham chiếu cho mentor…"
          />
        </div>
      )}

      <div className={panelClass}>
        <label className="mb-2 block text-sm font-semibold text-slate-800">
          Gợi ý / help (HTML, tùy chọn)
        </label>
        <AdminRichTextEditor
          value={helpContent}
          onChange={setHelpContent}
          placeholder="Hint cho Ask AI / học viên…"
        />
      </div>
    </div>
  )
}

export default AdminQuestionFormPage
